/**
 * Threat Intel (client notes 10/26, Threat Intel 3): a few sections, one per source, each laid out the same way: the
 * source's title and its items stack-ranked by severity. Pure, so the order is tested without a screen.
 */

export type IntelSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface IntelItem {
  id: string;
  severity: IntelSeverity;
  title: string;
  detail: string;
  /** How long ago the source published it. */
  minutesAgo: number;
  /** An incident of the tenant it bears on, when there is one. */
  incident?: string;
}

export interface IntelSection {
  /** What the section is titled: the source. */
  name: string;
  items: IntelItem[];
}

const RANK: Record<IntelSeverity, number> = { critical: 4, high: 3, medium: 2, low: 1 };

/** Most severe first; among equals the newest first, then by title so the order never depends on how they arrived. */
export function rankBySeverity(items: readonly IntelItem[]): IntelItem[] {
  return [...items].sort(
    (a, b) => RANK[b.severity] - RANK[a.severity] || a.minutesAgo - b.minutesAgo || a.title.localeCompare(b.title)
  );
}

/** "12 min ago", "3 h ago", "2 d ago". */
export function intelAge(minutesAgo: number): string {
  if (minutesAgo < 60) return `${Math.max(1, Math.round(minutesAgo))} min ago`;
  const hours = Math.round(minutesAgo / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}
